import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import FeaturesCarousel from '../components/FeaturesCarousel';
import PlaysVsBehaviorComparison from '../components/PlaysVsBehaviorComparison';
import { 
  Radio, 
  BarChart3, 
  Users, 
  Sparkles,
  CheckCircle,
  ArrowRight,
  Mic2,
  TrendingUp,
  Zap,
  Shield,
  Trophy,
  Music
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AboutPage = ({ isAuthenticated, currentUser }) => {
  const { t, language } = useLanguage();
  const [animationStep, setAnimationStep] = useState(0);
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Premium-fallback access (used by tools with `premium: true` but
  // no specific accessCheck) — admin or oloklirwmeno subscription.
  const hasPremiumAccess = currentUser?.role === 'admin' || 
    currentUser?.subscription_type === 'oloklirwmeno';

  // Check People's Chart access - admin or oloklirwmeno subscription
  const hasPeoplesChartAccess = currentUser?.role === 'admin' || 
    currentUser?.subscription_type === 'oloklirwmeno';

  // Check Song Analyzer access - admin or has assigned stations
  const hasSongAnalyzerAccess = currentUser?.role === 'admin' || 
    currentUser?.assigned_stations?.length > 0;

  // Staggered animation on mount
  useEffect(() => {
    const timers = [];
    for (let i = 1; i <= 5; i++) {
      timers.push(setTimeout(() => setAnimationStep(i), i * 200));
    }
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Memoize tools based on language to ensure re-render when language changes
  const tools = useMemo(() => [
    {
      id: 'on-air-content',
      icon: Sparkles,
      title: t('onAirContent') || 'On Air Content',
      subtitle: t('dailyContent') || 'Daily Content',
      description: t('onAirContentDesc') || 'Ready-to-use content for radio broadcasts.',
      features: [t('onAirFeature1') || '40+ thematic sections', t('onAirFeature2') || 'Daily updates', t('onAirFeature3') || 'Ready for on-air use'],
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-500/10',
      link: '/on-air-content',
      public: false
    },
    {
      id: 'campaign-analyzer',
      icon: BarChart3,
      title: t('campaignAnalyzer') || 'Campaign Analyzer',
      subtitle: t('campaignMetricsSubtitle') || 'Campaign Metrics',
      description: t('campaignAnalyzerDesc') || 'Calculate the effectiveness of your advertising campaigns.',
      features: [t('campaignFeature1') || 'ROI calculation', t('campaignFeature2') || 'Reach analysis', t('campaignFeature3') || 'Performance forecasts'],
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      link: '/metritis-kampanias',
      public: false
    },
    {
      id: 'listener-calculator',
      icon: Users,
      title: t('listenerCalculator') || 'Listener Calculator',
      subtitle: t('audienceMetricsSubtitle') || 'Audience Metrics',
      description: t('listenerCalculatorDesc') || 'Estimate your listener numbers based on various parameters.',
      features: [t('listenerFeature1') || 'Accurate estimates', t('listenerFeature2') || 'Detailed reports', t('listenerFeature3') || 'Period comparison'],
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-500/10',
      link: '/metritis-akroaton',
      public: false
    },
    {
      id: 'radio-top-songs',
      icon: Radio,
      title: t('radioTopSongs') || 'Top 1000 Radio Songs',
      subtitle: t('airplayChartsSubtitle') || 'Airplay Charts',
      description: t('radioTopSongsDesc') || 'Top 1000 songs from Greek radio.',
      features: [t('radioSongsFeature1') || 'Greek & International', t('radioSongsFeature2') || 'Weekly updates', t('radioSongsFeature3') || 'Historical data'],
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-500/10',
      link: '/radio-top-songs',
      public: false
    },
    {
      id: 'peoples-chart',
      icon: Trophy,
      title: "The People's Chart",
      subtitle: 'Top 300 Listener Favorites',
      description: 'Κατάταξη τραγουδιών βάσει αντίδρασης ακροατών. Ανακαλύψτε τι αρέσει πραγματικά στο κοινό σας.',
      features: ['Retention & Early Drop metrics', 'Πολλαπλοί σταθμοί', 'Export σε Excel'],
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-500/10',
      link: '/radio-chart',
      public: false,
      premium: true,
      accessCheck: 'peoples-chart'
    },
    {
      id: 'compare-stations',
      icon: BarChart3,
      title: 'Compare Stations',
      subtitle: 'Station Performance Comparison',
      description: 'Συγκρίνετε πώς αποδίδουν δύο σταθμοί με το κοινό τους. Βασισμένο σε metrics αντίδρασης ακροατών.',
      features: ['Side-by-side metrics', 'Common songs analysis', 'Daypart comparison'],
      color: 'from-purple-500 to-teal-500',
      bgColor: 'bg-purple-500/10',
      link: '/compare-stations',
      public: false,
      premium: true,
      accessCheck: 'compare-stations'
    },
    {
      id: 'song-analyzer',
      icon: Music,
      title: 'Song Analyzer',
      subtitle: 'Ανάλυση Τραγουδιών ανά Σταθμό',
      description: 'Αναλυτικά στατιστικά για κάθε τραγούδι που παίζει στον σταθμό σας. Retention, Early Drop, Play Times.',
      features: ['Real-time analytics', 'Ιστορικά δεδομένα', 'Export σε Excel'],
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-500/10',
      link: '/songs-analyzer',
      public: false,
      premium: true,
      accessCheck: 'song-analyzer'
    },
    {
      id: 'our-stations',
      icon: Mic2,
      title: t('ourStations') || 'Our Radio Stations',
      subtitle: t('ourStationsSubtitle') || 'Our Stations',
      description: t('ourStationsDesc') || 'Information about our partner radio stations.',
      features: [t('stationsFeature1') || 'Station network', t('stationsFeature2') || 'Coverage areas', t('stationsFeature3') || 'Live streaming'],
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-500/10',
      link: '/our-stations',
      public: false
    }
  ], [t]);

  const benefits = useMemo(() => [
    {
      icon: Zap,
      title: t('timeSaving') || 'Save Time',
      description: t('timeSavingDesc') || 'Automate content production and focus on the on-air part.'
    },
    {
      icon: TrendingUp,
      title: t('professionalResults') || 'Professional Results',
      description: t('professionalResultsDesc') || 'High-quality content ready for immediate on-air use.'
    },
    {
      icon: Shield,
      title: t('reliableData') || 'Reliable Data',
      description: t('reliableDataDesc') || 'Updated charts and statistics from reliable sources.'
    }
  ], [t]);

  // SEO keywords
  const seoTitle = language === 'el' 
    ? 'Radio Solutions Apps | Εργαλεία για Ραδιοφωνικούς Σταθμούς'
    : 'Radio Solutions Apps | Tools for Radio Stations';
  
  const seoDescription = language === 'el'
    ? 'Η πλατφόρμα για επαγγελματίες του ραδιοφώνου. Analytics, On Air Content, Charts, Playlist Analysis για ιδιοκτήτες, διευθυντές, παραγωγούς, playlisters και ομάδες πωλήσεων.'
    : 'The platform for radio professionals. Analytics, On Air Content, Charts, Playlist Analysis for owners, managers, producers, playlisters and sales teams.';

  const seoKeywords = language === 'el'
    ? 'ραδιοφωνικά εργαλεία, radio analytics, playlist analyzer, campaign analyzer, radio charts, ραδιοφωνικός σταθμός, on air content, ραδιοφωνική παραγωγή, radio solutions greece'
    : 'radio tools, radio analytics, playlist analyzer, campaign analyzer, radio charts, radio station, on air content, radio production, radio solutions greece';

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-[#F8F6FB] via-[#F5F0FA] to-[#F0EBF8] dark:from-[#1A0730] dark:via-[#2D0C4B] dark:to-[#1A0730] relative overflow-hidden about-page-container"
    >
      {/* Responsive Background Styles - Desktop με μικρόφωνο, Mobile χωρίς */}
      <style>{`
        /* Mobile: χωρίς μικρόφωνο, cover για να μην επαναλαμβάνεται */
        .about-page-container {
          background-image: linear-gradient(to bottom, rgba(26, 7, 48, 0.55), rgba(45, 12, 75, 0.65)), url('https://static.prod-images.emergentagent.com/jobs/24ae3cc6-4056-44a3-8028-4d9b45f666f4/images/8348488556076f457c12d9893dc3de95ea6990361ed691b3724b8e2f7d279c6b.png');
          background-size: cover;
          background-position: center top;
          background-repeat: no-repeat;
          background-attachment: scroll;
        }
        
        /* Desktop (lg και πάνω): με μικρόφωνο, αρχικές ρυθμίσεις */
        @media (min-width: 1024px) {
          .about-page-container {
            background-size: 120% auto;
            background-position: center -100px;
            background-attachment: fixed;
          }
        }
        
        @keyframes floatUpDown {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        @keyframes neonPulse {
          0%, 100% { 
            box-shadow: 0 0 15px rgba(180, 140, 255, 0.6), 0 0 30px rgba(138, 79, 219, 0.4), 0 0 45px rgba(138, 79, 219, 0.2), inset 0 0 15px rgba(180, 140, 255, 0.15);
            border-color: rgba(200, 170, 255, 0.8);
          }
          50% { 
            box-shadow: 0 0 25px rgba(200, 170, 255, 0.8), 0 0 50px rgba(138, 79, 219, 0.6), 0 0 75px rgba(138, 79, 219, 0.4), inset 0 0 25px rgba(200, 170, 255, 0.25);
            border-color: rgba(220, 200, 255, 0.95);
          }
        }
        @keyframes waveMove {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .bokeh-animate {
          animation: floatUpDown 5s ease-in-out infinite;
        }
        .bokeh-animate:nth-child(2) { animation-delay: 0.8s; animation-duration: 6s; }
        .bokeh-animate:nth-child(3) { animation-delay: 1.6s; animation-duration: 4.5s; }
        .bokeh-animate:nth-child(4) { animation-delay: 2.4s; animation-duration: 7s; }
        .bokeh-animate:nth-child(5) { animation-delay: 3.2s; animation-duration: 5.5s; }
        .on-air-neon {
          animation: neonPulse 2.5s ease-in-out infinite;
        }
        .wave-path {
          animation: waveMove 8s linear infinite;
        }
        .wave-path-2 {
          animation: waveMove 10s linear infinite;
          animation-delay: -2s;
        }
        .wave-path-3 {
          animation: waveMove 12s linear infinite;
          animation-delay: -4s;
        }
      `}</style>

      {/* Animated Bokeh Balls */}
      <div className="fixed inset-0 pointer-events-none z-[1] hidden lg:block">
        <div className="bokeh-animate absolute top-40 left-[12%] w-4 h-4 rounded-full bg-purple-400/40 blur-[2px]"></div>
        <div className="bokeh-animate absolute top-60 right-[25%] w-3 h-3 rounded-full bg-purple-300/35 blur-[1px]"></div>
        <div className="bokeh-animate absolute top-[55%] left-[8%] w-3 h-3 rounded-full bg-pink-400/40 blur-[1px]"></div>
        <div className="bokeh-animate absolute top-[35%] right-[15%] w-5 h-5 rounded-full bg-purple-500/30 blur-[2px]"></div>
        <div className="bokeh-animate absolute bottom-[35%] left-[20%] w-4 h-4 rounded-full bg-purple-300/35 blur-[1px]"></div>
      </div>

      {/* Animated Wave Lines - Curved waveforms moving left to right */}
      <div className="fixed inset-0 pointer-events-none z-[1] hidden lg:block overflow-hidden">
        <svg className="absolute top-[30%] left-0 w-[300%] h-40 opacity-40" viewBox="0 0 1200 100" preserveAspectRatio="none">
          <path 
            className="wave-path"
            d="M0,50 Q75,20 150,50 T300,50 T450,50 T600,50 T750,50 T900,50 T1050,50 T1200,50"
            fill="none"
            stroke="rgba(180, 140, 255, 0.6)"
            strokeWidth="2"
          />
        </svg>
        <svg className="absolute top-[45%] left-0 w-[300%] h-40 opacity-35" viewBox="0 0 1200 100" preserveAspectRatio="none">
          <path 
            className="wave-path-2"
            d="M0,50 Q100,10 200,50 T400,50 T600,50 T800,50 T1000,50 T1200,50"
            fill="none"
            stroke="rgba(200, 160, 255, 0.5)"
            strokeWidth="1.5"
          />
        </svg>
        <svg className="absolute top-[60%] left-0 w-[300%] h-40 opacity-30" viewBox="0 0 1200 100" preserveAspectRatio="none">
          <path 
            className="wave-path-3"
            d="M0,50 Q60,30 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50"
            fill="none"
            stroke="rgba(220, 180, 255, 0.45)"
            strokeWidth="2.5"
          />
        </svg>
      </div>

      {/* Clock - Top Left - Positioned higher but visible */}
      <div className="fixed top-16 left-[4%] z-[2] pointer-events-none hidden lg:block">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/24ae3cc6-4056-44a3-8028-4d9b45f666f4/images/8348488556076f457c12d9893dc3de95ea6990361ed691b3724b8e2f7d279c6b.png"
          alt=""
          className="w-32 h-32 object-contain opacity-0"
          style={{ visibility: 'hidden' }}
        />
        <div 
          className="absolute top-0 left-0 w-32 h-32 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(60,40,80,0.9) 0%, rgba(45,30,65,0.95) 40%, rgba(30,20,50,1) 100%)',
            boxShadow: '0 0 20px rgba(138, 79, 219, 0.3), inset 0 0 30px rgba(0,0,0,0.5)',
            border: '3px solid rgba(100, 80, 130, 0.6)'
          }}
        >
          {/* Clock face */}
          <div className="absolute inset-2 rounded-full border border-purple-400/30">
            {/* Clock numbers */}
            <span className="absolute top-1 left-1/2 -translate-x-1/2 text-purple-200/80 text-xs font-bold">XII</span>
            <span className="absolute top-1/2 right-1 -translate-y-1/2 text-purple-200/80 text-xs font-bold">III</span>
            <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-purple-200/80 text-xs font-bold">VI</span>
            <span className="absolute top-1/2 left-1 -translate-y-1/2 text-purple-200/80 text-xs font-bold">IX</span>
            {/* Clock hands */}
            <div className="absolute top-1/2 left-1/2 w-0.5 h-8 bg-purple-300/90 origin-bottom -translate-x-1/2 -translate-y-full rotate-[-30deg]"></div>
            <div className="absolute top-1/2 left-1/2 w-0.5 h-6 bg-purple-400/80 origin-bottom -translate-x-1/2 -translate-y-full rotate-[60deg]"></div>
            <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-purple-300 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          </div>
        </div>
      </div>

      {/* ON AIR Sign - CSS Neon Box Design - Smaller size to fit properly */}
      <div className="fixed top-16 right-[22%] z-[2] pointer-events-none hidden lg:block">
        <div 
          className="on-air-neon bg-purple-900/70 border-2 border-purple-300/80 rounded-lg px-6 py-3"
        >
          <span 
            className="text-purple-100 font-bold text-xl tracking-[0.3em]"
            style={{ 
              textShadow: '0 0 10px rgba(220, 200, 255, 0.9), 0 0 25px rgba(180, 140, 255, 0.7), 0 0 40px rgba(138, 79, 219, 0.5), 0 0 60px rgba(138, 79, 219, 0.3)'
            }}
          >
            ON AIR
          </span>
        </div>
      </div>
      {/* SEO Meta Tags - Title inherited from index.html (Radio Solutions Apps) */}
      <Helmet>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://radio-apps.online/" />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_deploy-lifeline/artifacts/b4jcqxjk_logo_transparent.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Radio Solutions Apps" />
        <meta property="og:locale" content={language === 'el' ? 'el_GR' : 'en_US'} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://radio-apps.online/" />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_deploy-lifeline/artifacts/b4jcqxjk_logo_transparent.png" />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://radio-apps.online/" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Radio Solutions Greece" />
      </Helmet>

      {/* Custom animations */}
      <style>{`
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
        @keyframes gradient {
          0% { background-position: 0% center; }
          50% { background-position: 100% center; }
          100% { background-position: 0% center; }
        }
        @keyframes shimmer {
          0% { text-shadow: 0 0 10px rgba(255,255,255,0.3); }
          50% { text-shadow: 0 0 20px rgba(255,255,255,0.6), 0 0 40px rgba(107,63,160,0.4); }
          100% { text-shadow: 0 0 10px rgba(255,255,255,0.3); }
        }
        .animate-float-slow { animation: floatSlow 3s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulseGlow 2s ease-in-out infinite; }
        .animate-gradient { animation: gradient 3s ease infinite; }
        .animate-shimmer { animation: shimmer 2s ease-in-out infinite; }
      `}</style>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden bg-transparent">
        
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
          <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-[#FF9F0A] rounded-full animate-pulse" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            
            {/* Animated Logo */}
            <div className={`mb-8 relative transition-all duration-700 ${animationStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
              <div className="absolute inset-0 bg-[#6B3FA0] blur-2xl opacity-40 rounded-full animate-pulse-glow mx-auto" style={{width: '180px', height: '180px', left: '50%', transform: 'translateX(-50%)'}}></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_deploy-lifeline/artifacts/b4jcqxjk_logo_transparent.png"
                alt="Radio Solutions Logo"
                className="w-36 h-36 sm:w-44 sm:h-44 object-contain relative z-10 drop-shadow-2xl animate-float-slow mx-auto"
              />
            </div>
            
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6 transition-all duration-500 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <Radio className="w-5 h-5 text-[#FF9F0A]" />
              <span className="text-sm font-medium text-white/90">
                Radio Solutions Greece
              </span>
            </div>
            
            {/* H1 - Main Title */}
            <h1 className={`text-3xl lg:text-5xl xl:text-6xl font-bold mb-4 text-white transition-all duration-500 ${animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('radioSolutionsApps') || 'Radio Solutions Apps'}
            </h1>
            
            {/* Subtitle - Roles */}
            <p className={`text-lg lg:text-xl text-transparent bg-clip-text bg-gradient-to-r from-[#FF9F0A] via-[#FFB84D] to-[#FF9F0A] bg-[length:200%_auto] animate-gradient font-semibold mb-6 transition-all duration-500 delay-100 ${animationStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('forRadioProfessionals') || 'For Owners, Directors, Producers, Playlisters & Sales Teams'}
            </p>
            
            {/* H2 */}
            <h2 className={`text-xl lg:text-2xl font-bold text-white/90 mb-4 transition-all duration-500 delay-150 ${animationStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('dataYouNeed') || 'The Data You Need, the Tools You Deserve'}
            </h2>
            
            {/* H3 */}
            <p className={`text-base lg:text-lg text-purple-200 mb-2 transition-all duration-500 delay-200 ${animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('analyticsContentCharts') || 'Analytics, Content & Charts for the modern radio station.'}
            </p>
            
            {/* Tagline with shimmer */}
            <p className={`text-xl lg:text-2xl font-bold text-white italic animate-shimmer mb-8 transition-all duration-500 delay-250 ${animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {t('radioFinallyMeasured') || 'Radio finally measured.'}
            </p>
            
            {/* Divider */}
            <div className={`w-24 h-1 bg-gradient-to-r from-[#6B3FA0] via-[#8A4FDB] to-[#FF9F0A] rounded-full mb-8 mx-auto transition-all duration-700 ${animationStep >= 5 ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
          </div>
        </div>
      </section>
      
      {/* NEW: Friendly Demo Section */}
      <section className="py-12 lg:py-16 bg-gradient-to-b from-transparent to-black/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            
            {/* Friendly Title */}
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              {t('toolsForRadio')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D]">
                {t('withoutHeadache')}
              </span>
            </h3>
            
            {/* Subtitle */}
            <p className="text-lg text-white/80 mb-3">
              {t('suiteForProfessionals')}
            </p>
            <p className="text-base text-white/60 mb-6">
              {t('normalIfDifficult')}
            </p>
            
            {/* Support Text */}
            <p className="text-base text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('freeDemoText').split('{freeDemo}')[0]}
              <span className="text-[#FF9F0A] font-semibold">{t('freeDemo')}</span>
              {t('freeDemoText').split('{freeDemo}')[1]}
            </p>
            
            {/* CTA Button */}
            <div className="flex justify-center mb-4">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] hover:from-[#FFB84D] hover:to-[#FF9F0A] text-white font-semibold px-8 py-4 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300"
                >
                  👉 {t('wantDemo')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            
            {/* Micro-message */}
            <p className="text-sm text-white/50 italic">
              💡 {t('noTechKnowledge')}
            </p>
            
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 lg:py-16 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="text-center p-6"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 text-white mb-4 shadow-lg">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-purple-200/90">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plays vs Listener Behavior — Why we differ */}
      <PlaysVsBehaviorComparison />

      {/* Tools Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('ourTools') || 'Our Tools'}
            </h2>
            <p className="text-lg text-purple-200/90 max-w-2xl mx-auto">
              {t('toolsSuiteDescription') || 'A complete suite of tools designed exclusively for radio stations'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {tools.map((tool) => {
              const Icon = tool.icon;
              // Determine access based on tool type
              let isAccessible = tool.public;
              if (!isAccessible && isAuthenticated) {
                if (tool.accessCheck === 'peoples-chart') {
                  isAccessible = hasPeoplesChartAccess;
                } else if (tool.accessCheck === 'song-analyzer') {
                  isAccessible = hasSongAnalyzerAccess;
                } else if (tool.premium) {
                  isAccessible = hasPremiumAccess;
                } else {
                  isAccessible = true; // Non-premium tools accessible to all authenticated users
                }
              }
              
              return (
                <div
                  key={tool.id}
                  className={`
                    relative group rounded-2xl p-6 
                    bg-white dark:bg-[#2D0C4B]/50 
                    border border-gray-200 dark:border-[#6B3FA0]/30
                    shadow-lg hover:shadow-xl transition-all duration-300
                    ${!isAccessible ? 'opacity-75' : ''}
                  `}
                >
                  {/* Premium badge */}
                  {tool.premium && (
                    <div className="absolute -top-3 -right-3 px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
                      PRO
                    </div>
                  )}
                  
                  {/* Icon */}
                  <div className={`
                    inline-flex items-center justify-center w-14 h-14 rounded-2xl 
                    bg-gradient-to-br ${tool.color} text-white mb-4 shadow-lg
                    group-hover:scale-110 transition-transform duration-300
                  `}>
                    <Icon className="w-7 h-7" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-3">
                    {tool.subtitle}
                  </p>
                  
                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                    {tool.description}
                  </p>
                  
                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {tool.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA Button - Each tool goes to its own page */}
                  <Link to={tool.link} onClick={() => window.scrollTo(0, 0)} className="block">
                    <Button 
                      className={`w-full bg-gradient-to-r ${tool.color} hover:opacity-90 text-white`}
                    >
                      {t('goToApp') || 'Μετάβαση'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
          
          {/* "Ξεκίνα Δωρεάν" Button - After tools - Goes to /trial-signup */}
          <div className="mt-12 text-center">
            <Link to="/trial-signup" onClick={() => window.scrollTo(0, 0)}>
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] hover:from-[#FFB84D] hover:to-[#FFC966] text-[#2D0C4B] font-bold px-10 py-6 text-lg shadow-xl hover:shadow-[#FF9F0A]/40 hover:scale-105 transition-all duration-300"
              >
                🚀 {t('startFreeTrial') || 'Ξεκίνα Δωρεάν Δοκιμή'}
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Preview Section — links to full /services page */}
      <section
        className="py-16 lg:py-20 bg-gradient-to-b from-transparent via-black/20 to-transparent"
        data-testid="services-preview-section"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="rounded-3xl border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-900/30 via-purple-900/20 to-[#2D0C4B]/40 backdrop-blur-sm p-8 md:p-12 lg:p-14 relative overflow-hidden">
              {/* Decorative orb */}
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-fuchsia-500/15 rounded-full blur-3xl pointer-events-none" />

              <div className="relative">
                {/* Header */}
                <div className="text-center mb-10">
                  <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold uppercase tracking-wider rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/25">
                    {t('servicesEyebrow') || 'Υπηρεσίες · Services'}
                  </span>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">
                    {t('servicesHeroTitle') || 'Music Scheduling & Log Optimization'}
                  </h2>
                  <p className="text-purple-100/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
                    {t('servicesPreviewDesc') ||
                      'Πέρα από τα εργαλεία μας, παρέχουμε εξειδικευμένες υπηρεσίες που βοηθούν τους ραδιοφωνικούς σταθμούς να αυξήσουν την ακροαματικότητα, να βελτιώσουν το πρόγραμμά τους και να ενισχύσουν τα έσοδά τους.'}
                  </p>
                </div>

                {/* 4 mini service cards — title + desc only (truncated before ΠΕΡΙΛΑΜΒΑΝΕΙ:) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                  {[
                    { titleKey: 'svc1Title', descKey: 'svc1Desc' },
                    { titleKey: 'svc2Title', descKey: 'svc2Desc' },
                    { titleKey: 'svc3Title', descKey: 'svc3Desc' },
                    { titleKey: 'svc4Title', descKey: 'svc4Desc' },
                  ].map((svc, i) => (
                    <div
                      key={i}
                      className="flex flex-col rounded-xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 hover:border-fuchsia-400/40 transition-all duration-300"
                      data-testid={`services-preview-card-${i + 1}`}
                    >
                      <h3 className="text-base font-bold text-white mb-2 leading-snug">
                        {t(svc.titleKey)}
                      </h3>
                      <p className="text-sm text-purple-100/80 leading-relaxed mb-4 flex-grow">
                        {t(svc.descKey)}
                      </p>
                      <Link
                        to="/services"
                        onClick={() => window.scrollTo(0, 0)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-300 hover:text-fuchsia-200 transition-colors"
                        data-testid={`services-preview-card-${i + 1}-learn-more`}
                      >
                        {t('svcLearnMore') || 'Μάθετε Περισσότερα'}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ))}
                </div>

                {/* Final CTA */}
                <div className="flex justify-center">
                  <Link
                    to="/services"
                    onClick={() => window.scrollTo(0, 0)}
                    data-testid="services-preview-cta"
                  >
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white font-semibold px-7 py-5 text-base shadow-lg hover:shadow-fuchsia-500/40 hover:scale-105 transition-all duration-300 whitespace-nowrap"
                    >
                      {t('readMore') || 'Διαβάστε Περισσότερα'}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase Carousel */}
      <section className="py-16 lg:py-24 bg-transparent">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('seeToolsInAction')}
            </h2>
            <p className="text-lg text-purple-200/90 max-w-2xl mx-auto">
              {t('allInOneTool')}
            </p>
          </div>
          
          <FeaturesCarousel />
        </div>
      </section>

      {/* CTA Section - Only for non-authenticated */}
      {!isAuthenticated && (
        <section className="py-16 lg:py-24 bg-gradient-to-br from-[#4A1D7A] to-[#2D0C4B]">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('readyToStart') || 'Ready to start?'}
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
              {t('signUpToday') || 'Sign up today and get access to all our tools'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing" onClick={() => window.scrollTo(0, 0)}>
                <Button 
                  size="lg"
                  className="bg-white text-[#4A1D7A] hover:bg-gray-100 px-8"
                >
                  {t('viewPackages') || 'View Packages'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/trial-signup" onClick={() => window.scrollTo(0, 0)}>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-8"
                >
                  {t('freeTrial') || 'Free Trial'}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer is rendered by App.js - removed from here to avoid duplication */}
    </div>
  );
};


export default AboutPage;
